<!--
  StoreEmailConfig.vue -- PR F2 Notifications matrix + PR F3 Email branding.

  Rebuilt from the legacy single-toggle "Send Order Confirmation Email"
  surface into an Event x Recipient toggle matrix backed by
  ``campaign.notification_preferences`` (JSONB column added in PR F1).

  Rows (events):       Order Placed / Order Shipped / Order Delivered /
                       Refund Issued
  Columns (recipients): Shopper / Manager / Client Admin

  Each cell is a v-switch bound to
  ``campaign.notification_preferences[event][recipient]``. Toggling a
  cell mutates the in-memory campaign and emits ``campaign-changed`` so
  the parent's autosave debounce fires. ``updateCampaign(campaign)``
  writes the full matrix back so the autosave RPC PATCH includes it --
  the backend's full-overwrite behavior on ``notification_preferences``
  (per PR F1) means we must round-trip the entire dict, not just the
  cell that changed.

  Per-event badging: ``EVENTS`` declares a ``badge`` slot per row so
  future events (e.g. pop-up-only triggers) can render a tag without
  template changes. MVP ships with no badges.

  PR F3 adds a second EditorCard "Email branding" below the matrix:
    - notification_from_name (text)    -> "Send emails as"
    - notification_reply_to_email      -> validated reply-to address
    - notification_branded_email (bool) -> use distributor brand color
  Backed by the scalar columns added in PR F1 (allowlist on
  update_campaign RPC). Empty strings normalize to NULL backend-side so
  the email render layer's NULL-fallback (use distributor name /
  no-reply default) stays simple.

  Security note: from-name and reply-to ultimately reach SMTP headers
  in the email-send path. To prevent CRLF (header) injection we strip
  CR/LF before binding on the write boundary in the frontend; backend
  defense-in-depth coercion lives in
  ``Store.update_campaign`` (see PR F3 backend changes).
-->
<template>
  <div class="store-email-config">
    <editor-card title="Notifications">
      <div class="notif-matrix" data-testid="notif-matrix">
        <div class="notif-matrix__header">
          <div class="notif-matrix__corner"></div>
          <div
            v-for="recipient in RECIPIENTS"
            :key="recipient.key"
            class="notif-matrix__col-label"
            :data-testid="`notif-col-${recipient.key}`"
          >
            {{ recipient.label }}
          </div>
        </div>
        <template v-for="group in EVENT_GROUPS" :key="group.key">
        <div
          class="notif-matrix__group"
          :data-testid="`notif-group-${group.key}`"
        >
          {{ group.label }}
        </div>
        <div
          v-for="event in group.events"
          :key="event.key"
          class="notif-matrix__row"
          :data-testid="`notif-row-${event.key}`"
        >
          <div class="notif-matrix__row-label">
            <span>{{ event.label }}</span>
            <span
              v-if="event.badge"
              class="notif-matrix__badge"
              :data-testid="`notif-badge-${event.key}`"
              >{{ event.badge }}</span
            >
          </div>
          <div
            v-for="recipient in RECIPIENTS"
            :key="recipient.key"
            class="notif-matrix__cell"
            :data-testid="`notif-cell-${event.key}-${recipient.key}`"
          >
            <v-switch
              :model-value="prefValue(event.key, recipient.key)"
              :disabled="disableFields"
              density="compact"
              hide-details
              color="primary"
              @update:modelValue="onToggle(event.key, recipient.key, $event)"
            />
          </div>
        </div>
        </template>
      </div>
    </editor-card>

    <editor-card
      title="Sender identity"
      description="What shoppers see in their inbox -- the name emails come from and where replies land. Shared across every email."
    >
      <div class="email-branding">
        <div class="email-branding__field">
          <v-text-field
            data-testid="notif-from-name"
            :model-value="campaign && campaign.notification_from_name"
            :disabled="disableFields"
            label="Send emails as"
            :placeholder="fromNamePlaceholder"
            density="compact"
            hide-details="auto"
            @update:modelValue="onFromNameInput"
          />
          <p class="email-branding__help">
            Leave blank to use your distributor name on outgoing emails.
          </p>
        </div>

        <div class="email-branding__field">
          <v-text-field
            data-testid="notif-reply-to"
            :model-value="campaign && campaign.notification_reply_to_email"
            :disabled="disableFields"
            label="Reply-to email"
            type="email"
            placeholder="If shoppers reply to a notification, where should it go?"
            :rules="replyToRules"
            density="compact"
            hide-details="auto"
            @update:modelValue="onReplyToInput"
          />
        </div>

        <div
          class="email-branding__toggle"
          data-testid="notif-branded-email"
        >
          <div class="email-branding__toggle-label">
            <div class="email-branding__toggle-title">
              Use distributor brand color in email template
            </div>
            <div class="email-branding__toggle-help">
              Headers and accents pick up your distributor brand color.
            </div>
          </div>
          <v-switch
            :model-value="!!(campaign && campaign.notification_branded_email)"
            :disabled="disableFields"
            density="compact"
            hide-details
            color="primary"
            @update:modelValue="onBrandedEmailToggle"
          />
        </div>
      </div>
    </editor-card>

    <!--
      Email templates (PR-4). The per-type subject/body editor + live preview.
      Coexists with the matrix + sender-identity card above per the design
      (planning/design-handoffs/handoff_2026-07-22/2026-07-22-email-notifications;
      ADR 0057). It reads sender identity + branded-email + header image from
      the shared campaign object so the preview stays in sync with the card
      above (single source of truth). Persists per-type copy to the dedicated
      PR-2 endpoint; header image to the store's email_image override.
    -->
    <div class="store-email-config__templates-head">
      <span class="store-email-config__templates-lbl">Email templates</span>
      <span class="store-email-config__templates-ln" />
    </div>
    <p class="store-email-config__templates-lede">
      Pick an email, customize the copy, and preview it with sample data. Sender
      identity and header image apply to every email; copy and available fields
      are set per email.
    </p>
    <store-email-templates
      :campaign="campaign"
      :disable-fields="disableFields"
      @campaign-changed="$emit('campaign-changed')"
    />
  </div>
</template>

<script>
import EditorCard from "./components/EditorCard.vue";
import StoreEmailTemplates from "./StoreEmailTemplates.vue";
import { email as emailRule } from "@/utils/validation";

/**
 * Strip CR / LF from a string. SMTP headers are terminated by CRLF,
 * so any user-controlled value that lands in From: or Reply-To: must
 * have those bytes stripped before it leaves the client. This is the
 * frontend half of the defense; the backend update_campaign RPC
 * applies the same coercion as defense-in-depth.
 *
 * We strip (not blank) so the non-CRLF payload still round-trips --
 * a user who paste-bombs a name with stray whitespace shouldn't lose
 * their typed content silently.
 */
function stripCRLF(value) {
    if (value == null) return value;
    return String(value).replace(/[\r\n]+/g, "");
}

// Mirrors api/src/models/store.py::DEFAULT_NOTIFICATION_PREFERENCES.
// Kept here as a fallback when the campaign prop is hydrated without a
// notification_preferences value (e.g. legacy rows pre-migration or a
// brand-new campaign whose hydration hasn't completed); the backend
// default normally fills this in on row creation.
const DEFAULT_PREFS = () => ({
  order_placed: { shopper: true, manager: false, client_admin: false },
  order_shipped: { shopper: true, manager: false, client_admin: false },
  order_delivered: { shopper: false, manager: false, client_admin: false },
  // A refund is a shopper-facing confirmation, so it defaults shopper ON
  // (mirrors order_placed). An existing store whose stored prefs predate
  // this row simply reads the toggle OFF (prefValue returns false on a
  // missing key) until the Owner saves -- no regression, expected.
  refund_issued: { shopper: true, manager: false, client_admin: false },
  // ---- ORDER BATCHING ---------------------------------------------------
  // Defaults mirror DEFAULT_NOTIFICATION_PREFERENCES exactly; the
  // reasoning for each per-role value lives there (models/store.py) and
  // is deliberately NOT duplicated here -- one place to argue about, one
  // place to change.
  batch_holding: { shopper: false, manager: true, client_admin: false },
  batch_release_approaching: {
    shopper: false,
    manager: true,
    client_admin: false,
  },
  batch_auto_released: { shopper: false, manager: true, client_admin: false },
  batch_charge_failed: { shopper: true, manager: true, client_admin: false },
  batch_released: { shopper: false, manager: true, client_admin: false },
  batch_order_edited: { shopper: true, manager: false, client_admin: false },
});

// Event rows, GROUPED. The ``badge`` slot is optional and reserved for
// future per-event tags (e.g. "Pop-Up only"); MVP ships with all badges
// unset.
//
// WHY GROUPS. The matrix was four flat rows; adding the six live batching
// events would have made it ten flat toggles, which reads as a wall of
// switches with no indication that the second half only applies to stores
// running batched release. Grouping is the same shape the account-level
// roster used (``event_roster.py`` carries a ``group`` per event), so this
// follows the existing convention rather than inventing a mechanism.
//
// WHY THE BATCH ROWS ARE HERE AT ALL. They are already LIVE on the
// backend -- rows in DEFAULT_NOTIFICATION_PREFERENCES whose senders
// consult ``gating.should_notify`` (services/batch_*.py). Until now this
// component's EVENTS list stopped at the original four, so the toggles
// governing mail that IS being sent rendered nowhere and a distributor
// could neither see nor change them. Jake, 2026-08-20: batch notices are
// ORDINARY ROWS in this matrix, configured exactly like the originals.
const EVENT_GROUPS = [
  {
    key: "orders",
    label: "Orders",
    events: [
      { key: "order_placed", label: "Order Placed", badge: "" },
      { key: "order_shipped", label: "Order Shipped", badge: "" },
      { key: "order_delivered", label: "Order Delivered", badge: "" },
      { key: "refund_issued", label: "Refund Issued", badge: "" },
    ],
  },
  {
    key: "batching",
    label: "Order batching",
    events: [
      { key: "batch_holding", label: "Batch Ready to Review", badge: "" },
      {
        key: "batch_release_approaching",
        label: "Release Approaching",
        badge: "",
      },
      { key: "batch_auto_released", label: "Batch Auto-Released", badge: "" },
      { key: "batch_charge_failed", label: "Charge Failed", badge: "" },
      { key: "batch_released", label: "Batch Released", badge: "" },
      { key: "batch_order_edited", label: "Order Edited", badge: "" },
    ],
  },
];

// Flattened view. Retained because the save path round-trips the whole
// matrix and several helpers iterate events without caring about groups.
const EVENTS = EVENT_GROUPS.flatMap((group) => group.events);

const RECIPIENTS = [
  { key: "shopper", label: "Shopper" },
  { key: "manager", label: "Manager" },
  { key: "client_admin", label: "Client Admin" },
];

export default {
  name: "StoreEmailConfig",

  props: {
    campaign: Object,
    editMode: Boolean,
    disableFields: Boolean,
    // Retained for prop-contract compatibility with the parent's
    // section-notifications slot; both flags were used by the legacy
    // single-toggle disable logic and are no longer load-bearing on
    // the matrix. F3 may revive one or both for the identity cards.
    preventOrderDuplication: Boolean,
    restrictEmailDomain: Boolean,
  },

  components: {
    EditorCard,
    StoreEmailTemplates,
  },

  emits: ["campaign-changed"],

  data() {
    return {
      EVENTS,
      EVENT_GROUPS,
      RECIPIENTS,
      // Validation rules for the reply-to text-field. Blank is
      // allowed (the email-send layer falls back to a default), so
      // we only validate format when something has been typed.
      replyToRules: [
        (value) => {
          if (value == null || value === "") return true;
          return emailRule(value);
        },
      ],
    };
  },

  computed: {
    /**
     * Placeholder shown in the from-name field. Defaults to the
     * distributor's name so users can see what the email envelope
     * will look like if they leave the field blank.
     */
    fromNamePlaceholder() {
      const distributor = this.campaign && this.campaign.distributor;
      const name = (distributor && distributor.name) || "";
      return name || "Your distributor name";
    },
  },

  methods: {
    /**
     * Read the boolean for ``[event][recipient]`` from the campaign
     * prop's notification_preferences. Falls back to the default
     * matrix if the column is null (e.g. campaign not yet hydrated).
     */
    prefValue(event, recipient) {
      const prefs =
        (this.campaign && this.campaign.notification_preferences) ||
        DEFAULT_PREFS();
      const row = prefs[event];
      if (!row) return false;
      return !!row[recipient];
    },

    /**
     * Toggle handler for a single cell. Mutates the in-memory campaign
     * prop -- the parent owns the campaign object and consumes
     * ``campaign-changed`` to fire the autosave debounce. We also
     * ensure the prefs object + the row dict exist before write, in
     * case the column hydrated as null.
     */
    onToggle(event, recipient, value) {
      if (!this.campaign) return;
      if (!this.campaign.notification_preferences) {
        this.campaign.notification_preferences = DEFAULT_PREFS();
      }
      if (!this.campaign.notification_preferences[event]) {
        this.campaign.notification_preferences[event] = {
          shopper: false,
          manager: false,
          client_admin: false,
        };
      }
      this.campaign.notification_preferences[event][recipient] = !!value;
      this.$emit("campaign-changed");
    },

    /**
     * From-name input handler (PR F3). Strips CR/LF to prevent SMTP
     * header injection before binding, then mutates the in-memory
     * campaign and fires the autosave debounce.
     */
    onFromNameInput(value) {
      if (!this.campaign) return;
      this.campaign.notification_from_name = stripCRLF(value);
      this.$emit("campaign-changed");
    },

    /**
     * Reply-to email input handler (PR F3). Strips CR/LF to prevent
     * SMTP header injection before binding. Format validation is
     * separately enforced via ``replyToRules`` on the field itself --
     * even if validation fails, we still bind so the user sees their
     * own typed value while correcting it.
     */
    onReplyToInput(value) {
      if (!this.campaign) return;
      this.campaign.notification_reply_to_email = stripCRLF(value);
      this.$emit("campaign-changed");
    },

    /**
     * Branded-email toggle handler (PR F3). When ON, the email
     * template uses the distributor's brand_color for headers/accents.
     */
    onBrandedEmailToggle(value) {
      if (!this.campaign) return;
      this.campaign.notification_branded_email = !!value;
      this.$emit("campaign-changed");
    },

    /**
     * Returns this section's slice of the campaign for autosave.
     * Per PR F1, the backend overwrites the entire notification_preferences
     * JSONB on PATCH, so we MUST send the full matrix even when only
     * one cell changed -- otherwise unchanged cells would be lost.
     *
     * PR F3 adds the three scalar identity columns. We re-apply
     * stripCRLF on the way out as belt-and-suspenders against any
     * code path that may have written to the model directly.
     */
    getData() {
      const campaign = this.campaign || {};
      const data = {
        notification_preferences:
          campaign.notification_preferences || DEFAULT_PREFS(),
        notification_from_name: stripCRLF(campaign.notification_from_name),
        notification_reply_to_email: stripCRLF(
          campaign.notification_reply_to_email,
        ),
        notification_branded_email: !!campaign.notification_branded_email,
      };
      // The email header-image override lives on ``campaign.email_image`` and
      // is edited by the embedded StoreEmailTemplates card (upload sets a uuid,
      // Remove sets ""). It MUST be in this autosave slice or the change never
      // reaches the update_campaign PATCH -- without it, Remove appeared to do
      // nothing (the clear was dropped) and uploads did not survive refresh.
      // Only include the key when the child has actually set it (upload/remove),
      // so we never clobber a server value on an untouched page. Empty string is
      // a real value here (the intentional clear -> NULL server-side).
      if (
        campaign.email_image !== undefined &&
        campaign.email_image !== null
      ) {
        data.email_image = campaign.email_image;
      }
      return data;
    },

    /**
     * Tab-style save method. Conforms to the contract every section
     * component on the campaign editor exposes (see e.g.
     * CampaignSetup.updateCampaign): mutate the passed-in campaign
     * with this section's fields and return it. Called by
     * StoreAddEdit's saveAndExitCampaign / autosave loop.
     */
    updateCampaign(campaign) {
      Object.assign(campaign, this.getData());
      return campaign;
    },
  },
};
</script>

<style scoped>
.store-email-config {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-4, 16px);
}
.email-branding {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-4, 16px);
}
.email-branding__field {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-1, 4px);
}
.email-branding__help {
  margin: 0;
  font-size: 0.8rem;
  color: var(--tf-fg-muted, #6b7280);
}
.email-branding__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--tf-sp-3, 12px);
  padding: var(--tf-sp-2, 8px) 0;
}
.email-branding__toggle-label {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-1, 4px);
}
.email-branding__toggle-title {
  font-weight: 500;
}
.email-branding__toggle-help {
  font-size: 0.8rem;
  color: var(--tf-fg-muted, #6b7280);
}
.notif-matrix {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-2, 8px);
}
.notif-matrix__header {
  display: grid;
  grid-template-columns: 1fr repeat(3, minmax(96px, 120px));
  align-items: end;
  gap: var(--tf-sp-3, 12px);
  padding: 0 var(--tf-sp-2, 8px);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--tf-fg-muted, #6b7280);
}
.notif-matrix__corner {
  /* empty top-left cell above the event-label column */
}
.notif-matrix__col-label {
  text-align: center;
  font-weight: 600;
}
.notif-matrix__group {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--tf-text-muted, #6b7280);
  padding: var(--tf-sp-3, 12px) var(--tf-sp-2, 8px) var(--tf-sp-1, 4px);
}
.notif-matrix__row {
  display: grid;
  grid-template-columns: 1fr repeat(3, minmax(96px, 120px));
  align-items: center;
  gap: var(--tf-sp-3, 12px);
  padding: var(--tf-sp-2, 8px);
  border-radius: var(--tf-radius-sm, 8px);
}
.notif-matrix__row + .notif-matrix__row {
  border-top: 1px solid var(--tf-border, #e5e7eb);
}
.notif-matrix__row-label {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-2, 8px);
  font-weight: 500;
}
.notif-matrix__badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 999px;
  background: var(--tf-bg-muted, #f3f4f6);
  color: var(--tf-fg-muted, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.notif-matrix__cell {
  display: flex;
  justify-content: center;
  align-items: center;
}
.store-email-config__templates-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: var(--tf-sp-2, 8px) 0 4px;
}
.store-email-config__templates-lbl {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #78909c);
  white-space: nowrap;
}
.store-email-config__templates-ln {
  flex: 1;
  height: 1px;
  background: var(--tf-border, #e5e7eb);
}
.store-email-config__templates-lede {
  margin: 0 0 16px;
  max-width: 720px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--tf-fg-3, #546e7a);
}
</style>
